import { useState } from "react";

import { DonutChart } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { getValueAsPercentage } from "app/pages/keyword-research/keyword-analysis/pages/keywords-overview/serp-composition/serpCompositionUtils";

import { ShareTypeSwitcher, SwitcherOption } from "../shared/ShareTypeSwitcher";

import { deviceBreakdownConfig, switcherOptionToDataType } from "./configs/breakdownConfigs";
import { chartConfig } from "./configs/PerformanceAdPlacementChartConfig";
import { Legend } from "./Legend";
import { ShortEmptyState } from "./shortEmptyState";
import {
    BreakdownChartContainer,
    BreakdownChartWithLegendContainer,
    BreakdownLegendWrapper,
    BreakdownChartWrapper,
} from "./StyledComponents";
import { normalizePercentages } from "./utils/graphUtils";
import { getShareValueBySwitcherOption } from "./utils/utils";

interface IDisplayAdvertisingDeviceBreakdownWidgetProps {
    spendShare: number;
    impressionsShare: number;
    visitsShare: number;
    hasAdIntelMetrics: boolean;
    showBreakdownEmptyState: boolean;
    performanceError?: boolean;
}

export const DisplayAdvertisingDeviceBreakdownWidget = ({
    spendShare,
    impressionsShare,
    visitsShare,
    hasAdIntelMetrics,
    showBreakdownEmptyState,
    performanceError,
}: IDisplayAdvertisingDeviceBreakdownWidgetProps) => {
    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState<SwitcherOption>(() => {
        if (hasAdIntelMetrics === false) {
            return SwitcherOption.Visits;
        }
        const options = [SwitcherOption.Spend, SwitcherOption.Impressions, SwitcherOption.Visits];
        return (
            options.find((option) =>
                getShareValueBySwitcherOption(option, spendShare, impressionsShare, visitsShare),
            ) || SwitcherOption.Spend
        );
    });

    const handleSwitcherChange = (option: SwitcherOption) => {
        setSelectedSwitcherOption(option);
        const dataType = switcherOptionToDataType[option];
        allTrackers.dispatchTrackEvent("Metric Button", "switch", dataType);
    };

    const desktopShare = getShareValueBySwitcherOption(
        selectedSwitcherOption,
        spendShare,
        impressionsShare,
        visitsShare,
    );
    const mobileShare = 1 - desktopShare;
    const normalizedValues = normalizePercentages([desktopShare, mobileShare]);

    const deviceBreakdownData = deviceBreakdownConfig.map((config, index) => ({
        color: config.color,
        name: translate(config.translationKey),
        id: config.id,
        visible: true,
        y: normalizedValues[index],
        total: normalizedValues[index],
    }));

    const legendItems = deviceBreakdownConfig.map((config, index) => ({
        name: translate(config.translationKey),
        value: getValueAsPercentage(normalizedValues[index]),
        color: config.color,
    }));

    const shouldShowEmptyStateForAdIntelMetrics =
        hasAdIntelMetrics === false && selectedSwitcherOption !== SwitcherOption.Visits;

    const shouldShowEmptyState = showBreakdownEmptyState || shouldShowEmptyStateForAdIntelMetrics;

    return (
        <Flex
            flexDirection="column"
            height="100%"
        >
            <Flex
                padding="0 24px 16px 24px"
                justifyContent="flex-start"
            >
                <ShareTypeSwitcher
                    selectedSwitcherOption={selectedSwitcherOption}
                    setSelectedSwitcherOption={handleSwitcherChange}
                />
            </Flex>
            <BreakdownChartContainer>
                {shouldShowEmptyState ? (
                    <ShortEmptyState isError={performanceError} />
                ) : (
                    <BreakdownChartWithLegendContainer>
                        <BreakdownChartWrapper>
                            <DonutChart
                                data={[{ data: deviceBreakdownData }]}
                                config={chartConfig}
                                shouldPresentLegend={false}
                                innerSize="55%"
                            />
                        </BreakdownChartWrapper>
                        <BreakdownLegendWrapper>
                            <Legend
                                items={legendItems}
                                dataAutomation="device-breakdown-legend"
                            />
                        </BreakdownLegendWrapper>
                    </BreakdownChartWithLegendContainer>
                )}
            </BreakdownChartContainer>
        </Flex>
    );
};
